import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, RotateCcw, CheckCircle2, AlertTriangle, Pause } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { api, formatErr } from "@/lib/api";
import { compressImage, LOWRES_SIDE } from "@/lib/compress";

const CONCURRENCY = 6;
const MAX_MB = 25;
const TYPES = ["image/jpeg", "image/png", "image/webp"];
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

export const formatEta = (sec) => {
  if (!isFinite(sec) || sec <= 0) return "-";
  if (sec < 60) return `${Math.ceil(sec)} dtk`;
  const m = Math.ceil(sec / 60);
  return m < 60 ? `${m} mnt` : `${Math.floor(m / 60)} j ${m % 60} mnt`;
};

const STAGES = { lowres: ["Mengompres", "Mengunggah", "Memberi watermark"], hd: ["Mengunggah", "Menyimpan HD"] };

const StageRow = ({ item, stages }) => (
  <li className="flex items-center gap-3 py-1.5" data-testid="upload-inflight-item">
    <span className="flex-1 min-w-0 truncate text-xs text-zinc-700">{item.name}</span>
    <span className="flex items-center gap-1">
      {stages.map((st, i) => (
        <span key={st} className={`rounded-md px-1.5 py-0.5 text-[10px] font-semibold whitespace-nowrap transition-colors ${i < item.stage ? "bg-emerald-100 text-emerald-700" : i === item.stage ? "bg-blue-600 text-white animate-pulse" : "bg-zinc-100 text-zinc-400"}`} data-testid={`upload-stage-${i}`}>
          {st}{i === item.stage && st === "Mengunggah" && item.pct != null ? ` ${item.pct}%` : ""}
        </span>
      ))}
    </span>
  </li>
);

async function uploadOne(url, file, onStage) {
  for (let attempt = 0; attempt < 3; attempt++) {
    try {
      const fd = new FormData();
      fd.append(url.endsWith("/hd") ? "file" : "files", file, file.name);
      let lastPct = -1;
      await api.post(url, fd, {
        timeout: 180000,
        onUploadProgress: (ev) => {
          const pct = ev.total ? Math.floor((ev.loaded / ev.total) * 10) * 10 : null;
          if (pct === lastPct) return;
          lastPct = pct;
          onStage(pct === 100 ? "server" : "upload", pct);
        },
      });
      return null;
    } catch (e) {
      const status = e?.response?.status;
      if (status && status < 500 && status !== 429) return formatErr(e);
      if (attempt === 2) return formatErr(e);
      await sleep(1500 * (attempt + 1));
    }
  }
}

export const UploadPanel = ({ eventId, onDone, onActiveChange, mode = "lowres" }) => {
  const hd = mode === "hd";
  const url = hd ? `/events/${eventId}/hd` : `/events/${eventId}/photos`;
  const maxMb = hd ? 50 : MAX_MB;
  const tid = hd ? "hd-upload" : "upload";
  const inputRef = useRef();
  const stopRef = useRef(false);
  const [state, setState] = useState(null);
  const [failed, setFailed] = useState([]);
  const [drag, setDrag] = useState(false);
  const [inflight, setInflight] = useState({});
  const stages = STAGES[hd ? "hd" : "lowres"];
  const active = state && !state.finished;

  useEffect(() => {
    onActiveChange?.(!!active);
    if (!active) return;
    const warn = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [active]); // eslint-disable-line react-hooks/exhaustive-deps

  const start = async (fileList) => {
    const all = Array.from(fileList || []);
    const valid = all.filter((f) => TYPES.includes(f.type) && f.size <= maxMb * 1024 * 1024);
    const skipped = all.length - valid.length;
    if (skipped) toast.warning(`${skipped} file dilewati (bukan JPG/PNG/WEBP atau lebih dari ${maxMb}MB)`);
    if (!valid.length) return;
    stopRef.current = false;
    setFailed([]);
    const queue = [...valid];
    const t0 = Date.now();
    let done = 0, ok = 0, bytes = 0, firstOk = false;
    const errors = [];
    const tick = (finished = false) => {
      const sec = (Date.now() - t0) / 1000;
      setState({ total: valid.length, done, ok, finished, speed: done / Math.max(sec, 0.1), mbps: bytes / 1048576 / Math.max(sec, 0.1),
        eta: (valid.length - done) / Math.max(done / Math.max(sec, 0.1), 0.001) });
    };
    tick();
    let seq = 0;
    const setStage = (id, patch) => setInflight((prev) => {
      const next = { ...prev };
      if (patch) next[id] = { ...prev[id], ...patch };
      else delete next[id];
      return next;
    });
    const worker = async () => {
      while (queue.length && !stopRef.current) {
        const original = queue.shift();
        const id = ++seq;
        setStage(id, { name: original.name, stage: 0, pct: null });
        const file = hd ? original : await compressImage(original);
        const up = hd ? 0 : 1;
        if (!hd) setStage(id, { stage: up, pct: 0 });
        const err = await uploadOne(url, file, (phase, pct) => setStage(id, phase === "server" ? { stage: up + 1, pct: null } : { stage: up, pct }));
        setStage(id, null);
        done++;
        if (err) errors.push({ file: original, err });
        else {
          ok++;
          bytes += file.size;
          if (!firstOk) { firstOk = true; onDone?.(); }
        }
        tick();
      }
    };
    await Promise.all(Array.from({ length: CONCURRENCY }, worker));
    setFailed([...errors, ...queue.map((file) => ({ file, err: "Dihentikan" }))]);
    tick(true);
    if (inputRef.current) inputRef.current.value = "";
    toast.success(hd ? `${ok} file HD terkirim ke pembeli` : `${ok} foto terunggah & ber-watermark. Deteksi wajah berjalan di server, tab boleh ditutup.`);
    onDone?.();
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDrag(false);
    if (!active) start(e.dataTransfer.files);
  };

  return (
    <section className="mb-8 space-y-4">
      <div onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)} onDrop={onDrop}
        className={`rounded-2xl border-2 border-dashed p-6 flex flex-col sm:flex-row sm:items-center gap-4 transition-colors ${drag ? "border-blue-500 bg-blue-50" : "border-zinc-300 bg-white"}`} data-testid={`${tid}-dropzone`}>
        <span className={`h-12 w-12 shrink-0 rounded-xl grid place-items-center ${hd ? "bg-emerald-50 text-emerald-600" : "bg-blue-50 text-blue-600"}`}><Upload className="h-5 w-5" /></span>
        <div className="flex-1">
          <p className="font-bold text-zinc-900">{hd ? "Kirim file HD ke pembeli" : "Seret & lepas ribuan foto ke sini"}</p>
          <p className="text-sm text-zinc-500">
            {hd
              ? `Unggah file resolusi penuh (maks. ${maxMb}MB) dengan NAMA FILE SAMA seperti foto yang diunggah sebelumnya. Pembeli otomatis mendapat email & bisa mengunduh HD.`
              : `JPG, PNG, WEBP maks. ${MAX_MB}MB. Tiap foto dikompres ke low-res (maks. ${LOWRES_SIDE}px) di browser, diunggah, lalu langsung diberi watermark Akudifoto oleh server sebelum disimpan. File HD dikirim nanti setelah ada pembeli.`}
          </p>
        </div>
        <Button onClick={() => inputRef.current.click()} disabled={active} className={`rounded-xl h-11 ${hd ? "bg-emerald-600 hover:bg-emerald-700" : "bg-blue-600 hover:bg-blue-700"}`} data-testid={`${tid}-photos-button`}>
          <Upload className="mr-2 h-4 w-4" />{hd ? "Pilih file HD" : "Pilih foto"}
        </Button>
        <input ref={inputRef} type="file" multiple accept={TYPES.join(",")} className="hidden" onChange={(e) => start(e.target.files)} data-testid={`${tid}-photos-input`} />
      </div>
      {state && (
        <div className="rounded-2xl bg-white border border-zinc-200 p-5" data-testid={`${tid}-progress`}>
          <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
            <p className="text-sm font-medium" data-testid={`${tid}-progress-text`}>
              {state.finished ? <CheckCircle2 className="inline h-4 w-4 mr-1 text-emerald-600" /> : null}
              {state.finished ? "Unggahan selesai" : "Mengunggah ke server"} · {state.done}/{state.total} terkirim · {state.ok} berhasil
            </p>
            {active && <Button size="sm" variant="outline" className="rounded-lg" onClick={() => { stopRef.current = true; }} data-testid={`${tid}-stop-button`}><Pause className="mr-1 h-3.5 w-3.5" />Hentikan</Button>}
          </div>
          <Progress value={(state.done / state.total) * 100} />
          {active && Object.keys(inflight).length > 0 && (
            <ul className="mt-3 rounded-xl bg-zinc-50 border border-zinc-100 px-3 py-1 divide-y divide-zinc-100" data-testid={`${tid}-inflight`}>
              {Object.entries(inflight).map(([id, item]) => <StageRow key={id} item={item} stages={stages} />)}
            </ul>
          )}
          <p className="mt-2 text-xs text-zinc-500 font-mono-x" data-testid={`${tid}-speed`}>
            {state.speed.toFixed(1)} foto/dtk · {state.mbps.toFixed(1)} MB/dtk{active ? ` · sisa ± ${formatEta(state.eta)}` : ""}
          </p>
          {failed.length > 0 && (
            <div className="mt-4 rounded-xl bg-amber-50 border border-amber-200 p-3 text-sm" data-testid={`${tid}-failed-list`}>
              <div className="flex items-center justify-between gap-3">
                <p className="text-amber-800"><AlertTriangle className="inline h-4 w-4 mr-1" />{failed.length} foto gagal / belum diunggah</p>
                <Button size="sm" className="rounded-lg bg-amber-600 hover:bg-amber-700" onClick={() => start(failed.map((f) => f.file))} data-testid={`${tid}-retry-failed-button`}><RotateCcw className="mr-1 h-3.5 w-3.5" />Coba lagi</Button>
              </div>
              <ul className="mt-2 max-h-28 overflow-y-auto text-xs text-amber-900 space-y-0.5">
                {failed.slice(0, 50).map((f, i) => <li key={i} className="truncate">{f.file.name}: {f.err}</li>)}
              </ul>
            </div>
          )}
        </div>
      )}
    </section>
  );
};
